import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage as T } from 'react-intl';

import AddressSearchBar from '@otovo/shared/components/InterestRegistration/AddressSearch/AddressSearchBar';
import PoweredByOtovo from '@otovo/shared/components/PoweredByOtovo';
import { logInterest } from '@otovo/shared/lib/analytics/analytics';
import { broadcastHeight } from '@otovo/shared/lib/iframeAPI';
import { Cloud$HardwareTypeBatterySystem } from '@otovo/shared/types/cloudEnums';
import getFirstFromArray from '@otovo/shared/utils/getFirstFromArray';
import { useSearchParams } from 'next/navigation';

import { useGlobalConfig } from '@otovo/shared/hooks/configContext';
import m from './messages';
import { getSanityImages } from './sanityImages';

// VERTICAL_PADDING should be equvivalent to py-4 (1rem). Used to report correct
// height including padding on absolute positioned element
const VERTICAL_PADDING = 16;

const OPEN_SIZE = 520;
const CLOSED_SIZE = 192; // includes room for error message and Holu's consumption slider
const addressSearchSizes = {
  open: OPEN_SIZE,
  closed: CLOSED_SIZE,
};

const BreezeAddressSearch = () => {
  const { isFallback, query } = useRouter();
  const { context, debug, utm_source: utmSource } = query;
  const wrapperRef = useRef<HTMLDivElement>();

  const handleHeightChange = useCallback(
    (state: 'open' | 'closed') => {
      if (typeof window !== 'undefined' && context === 'iframe') {
        broadcastHeight(addressSearchSizes[state] + VERTICAL_PADDING, !!debug);
      }
    },
    [context, debug],
  );

  useEffect(() => {
    // Broadcast height on mount
    handleHeightChange('closed');
  }, [handleHeightChange]);

  const config = useGlobalConfig();

  const searchParams = useSearchParams();
  const salesflowQueryParam = searchParams.get('salesflow');
  const mainHardwareType =
    salesflowQueryParam === 'battery'
      ? ('battery_system' as Cloud$HardwareTypeBatterySystem)
      : undefined;

  if (isFallback) {
    return null;
  }

  // This is used by the scriptless iframe to avoid having a lot of whitespace
  // below the iframe.
  const includeContentBelowSearch = query?.contentBelow === 'true';

  const sanityImages = getSanityImages(getFirstFromArray(utmSource));

  return (
    <div
      ref={wrapperRef}
      className="mx-auto w-full max-w-[1536px] px-2 md:px-4"
      style={{
        paddingTop: VERTICAL_PADDING / 2,
        paddingBottom: VERTICAL_PADDING / 2,
      }}
    >
      <AddressSearchBar
        // Add key for force re-render when query params are picked up by Next.js
        key={`address-search-${context}`}
        submissionMethod="nativeForm"
        onInterestCreated={() =>
          logInterest({ locale: config.BU_CONFIG.locale })
        }
        onAddressesShown={handleHeightChange}
        mainHardwareType={mainHardwareType}
      />
      {includeContentBelowSearch && (
        <div className="mx-auto mt-12 w-full max-w-7xl px-2 md:mt-16 md:px-4">
          <div className="-mx-4 flex flex-wrap gap-3 md:mb-8 md:gap-4 lg:gap-0">
            <div className="w-full px-4 lg:mb-0 lg:w-1/3">
              <div className="flex gap-3 lg:flex-col">
                {sanityImages.housePin && (
                  <div className="order-1 hidden w-36 shrink-0 md:grid md:items-center lg:order-0 lg:w-2/3">
                    <img alt="" src={sanityImages.housePin} />
                  </div>
                )}
                <div>
                  <h2 className="mb-1 text-base font-bold text-lilac-90 md:text-base lg:text-lg">
                    <T {...m.stepOneTitle} />
                  </h2>
                  <p className="mb-0 max-w-prose text-sm text-grey-40 md:text-base">
                    <T {...m.stepOneBody} />
                  </p>
                </div>
              </div>
            </div>
            <div className="w-full px-4 lg:mb-0 lg:w-1/3">
              <div className="flex gap-3 lg:flex-col">
                {sanityImages.solarPanel && (
                  <div className="order-1 hidden w-36 shrink-0 md:grid md:items-center lg:order-0 lg:w-2/3">
                    <img alt="" src={sanityImages.solarPanel} />
                  </div>
                )}
                <div>
                  <h2 className="mb-1 text-base font-bold text-lilac-90 md:text-base lg:text-lg">
                    <T {...m.stepTwoTitle} />
                  </h2>
                  <p className="mb-0 max-w-prose text-sm text-grey-40 md:text-base">
                    <T {...m.stepTwoBody} />
                  </p>
                </div>
              </div>
            </div>
            <div className="w-full px-4 lg:w-1/3">
              <div className="flex gap-3 lg:flex-col">
                {sanityImages.houseWithPanels && (
                  <div className="order-1 hidden w-36 shrink-0 md:grid md:items-center lg:order-0 lg:w-2/3">
                    <img alt="" src={sanityImages.houseWithPanels} />
                  </div>
                )}
                <div>
                  <h2 className="mb-1 text-base font-bold text-lilac-90 md:text-base lg:text-lg">
                    <T {...m.stepThreeTitle} />
                  </h2>
                  <p className="mb-0 max-w-prose text-sm text-grey-40 md:text-base">
                    <T {...m.stepThreeBody} />
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="hidden md:block">
            <PoweredByOtovo />
          </div>
        </div>
      )}
    </div>
  );
};
export default BreezeAddressSearch;
